import { useState, useEffect, useRef } from 'react';
import useConvertLexicalScope from '../../hooks/useConvertLexicalScope';
import { saveAsDraft } from '../../../../apis/article.api';
import DataLayer from '../../../../utils/data-layer';

export function useAutoSave({
  editorStateRef,
  textLengthRef,
  metadata,
  contributors,
  profile,
  searchParams
}) {
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSaved, setLastSaved] = useState(null);
  const [articleId, setArticleId] = useState(searchParams.get('id') || null);

  const saveTimeoutRef = useRef(null);
  const maxIntervalRef = useRef(null);
  const isDirtyRef = useRef(false);
  const firstSaveCompletedRef = useRef(false);

  const convertLexicalScope = useConvertLexicalScope();

  const performSave = async () => {
    if (!editorStateRef.current || !isDirtyRef.current || metadata.isPublished) return;

    try {
      setSaveStatus('saving');

      const content = await convertLexicalScope({
        editorState: editorStateRef.current
      });
      const formattedContributors = contributors.map((contributor) => ({
        id: contributor.user.id,
        role: contributor.role.value,
        equityPercentage: contributor.equityPercentage || 0,
        isManual: contributor.isManual
      }));
      const isEditMode = articleId != null || searchParams.get('type') === 'edit';

      const allContributors = [
        ...formattedContributors,
        ...(isEditMode
          ? [{ id: profile.id, role: 'author', equityPercentage: 0, isManual: true }]
          : [])
      ];

      const payload = {
        ...metadata,
        articleId,
        topicIds: metadata?.topicIds?.map((topic) => topic.id) || [],
        contributors: allContributors,
        content,
        contentLength: textLengthRef.current
      };

      const response: any = await saveAsDraft(payload);
      // If this was the first save and we got a new ID back, store it
      if (!articleId && response && response?.id) {
        setArticleId(response.id);

        // Track article draft event for new articles
        const articleType = metadata?.writingType;
        DataLayer.trackArticleDraft(articleType, response.id);

        // If this is the very first save, update the URL
        // to include the new article ID without redirecting
        if (!firstSaveCompletedRef.current) {
          // Update URL with the new article ID without redirecting
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('id', response.id);
          newUrl.searchParams.set('step', '4');
          newUrl.searchParams.set('type', 'edit');
          if (newUrl.pathname.includes('/article/create')) {
            newUrl.pathname = newUrl.pathname.replace('/article/create', '/article/edit');
          }
          window.history.replaceState({}, '', newUrl.toString());

          firstSaveCompletedRef.current = true;
        }
      }

      setLastSaved(new Date());
      setSaveStatus('saved');
      isDirtyRef.current = false;

      return response;
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('error');
      throw error;
    }
  };

  const triggerSave = () => {
    if (saveStatus === 'saving' || textLengthRef?.current === 0) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (saveStatus === 'saved') {
      setSaveStatus('modified');
    }

    isDirtyRef.current = true;

    saveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, 3000); // Save 3 seconds after typing stops
  };

  // Setup time-based safety net
  useEffect(() => {
    maxIntervalRef.current = setInterval(() => {
      if (isDirtyRef.current && saveStatus !== 'saving') {
        performSave();
      }
    }, 60000); // Force save every minute if changes exist

    return () => {
      if (maxIntervalRef.current) {
        clearInterval(maxIntervalRef.current);
      }
    };
  }, [saveStatus]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (maxIntervalRef.current) {
        clearInterval(maxIntervalRef.current);
      }
    };
  }, []);

  return {
    saveStatus,
    lastSaved,
    articleId,
    triggerSave,
    forceSave: () => performSave()
  };
}
